import { isLocale, parseLocale } from 'shared/locales'
import type { AppProps } from 'shared/types'
import { getCookie } from '@/lib/cookies'
import { create } from 'zustand'

/** Which drawing the report's radar uses: the classic lines, or the ring-per-indicator design the landing's hero shows. */
export type RadarStyle = 'classic' | 'rings'

const RADAR_STYLE_KEY = 'radar-style'

// A private window (or blocked site data) throws on access, and this runs at module init, so it must not.
const storedRadarStyle = (): RadarStyle => {
  try {
    return localStorage.getItem(RADAR_STYLE_KEY) === 'rings' ? 'rings' : 'classic'
  } catch {
    return 'classic'
  }
}

export const useAppStore = create<{
  store: AppProps
  // Partial: setStore deep-merges, so callers can update just accentColor/darkMode/button.
  setStore: (value: Partial<Omit<AppProps, 'customize'>> & { customize?: Partial<AppProps['customize']> }) => void
  newVersionAvailable: boolean
  setNewVersionAvailable: (value: boolean) => void
  // Read it via useHydrated() to gate SSR'd controls, so pre-hydration clicks aren't silently dropped.
  hydrated: boolean
  setHydrated: () => void
  // Super-admin preview switch. Read from storage at load but only APPLIED behind useHydrated(), since the
  // report radar is server-rendered and the server has no access to this preference.
  radarStyle: RadarStyle
  setRadarStyle: (value: RadarStyle) => void
  // Latch: the gate only shows while unanswered, so without it the registration step count would shift.
  peopleStepShown: boolean
  markPeopleStepShown: () => void
  // In-memory only: "Close" must reopen the guide on the next entry; only "Don't show again" persists (tour=1).
  welcomeDismissed: boolean
  dismissWelcome: () => void
  resetWelcome: () => void
}>((set) => ({
  store: {
    sdk: false,
    app: undefined,
    clientId:
      typeof window !== 'undefined'
        ? (new URLSearchParams(window.location.search).get('clientId') ?? undefined)
        : undefined,
    userToken:
      typeof window !== 'undefined'
        ? (new URLSearchParams(window.location.search).get('token') ?? undefined)
        : undefined,
    extension: undefined,
    callback: () => undefined,
    routeTo: undefined,
    applicantData: undefined,
    companyData: undefined,
    jobData: undefined,
    profiles: undefined,
    customize: {
      // null = no live SDK override, so a reload paints the accent from the suspense user and a postMessage still wins.
      accentColor: null,
      primaryFont: "'Plus Jakarta Sans', fallback-font, sans-serif",
      secondaryFont: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      taskColor: '#0bd',
      peopleColor: '#9c5',
      contextColor: '#fc4',
      compareColor: '#f60',
      positioningColor: '#4b9faa',
      primaryErrorColor: '#F02849',
      secondaryErrorColor: '#EC5990',
      tourColor: '#357cf0',
      doneColor: '#25C133',
      // From SDK
      disableLogo: false,
      disableReportDescription: false,
      defaultReportPage: 'detailed',
      loginUrl: undefined,
      // accentColor/darkMode/button fields come from user.app via the AppShell sync (see above).
      darkMode: undefined,
      applicantButtonExit: undefined,
      jobButtonExit: undefined,
      enableJobAddApplicantButton: true,
    },
    // A stale `theme=undefined` cookie would otherwise read back as the string "undefined" and count as an explicit mode.
    darkMode: ((t) => (t === 'dark' || t === 'light' ? t : 'system'))(getCookie('theme')),
    resolvedDarkMode: 'light',
    // Must match the locale the server already rendered, or every translated node mismatches at hydration (#418).
    locale: (() => {
      if (typeof window === 'undefined') {
        const cookieLang = getCookie('language')
        return isLocale(cookieLang) ? cookieLang : parseLocale({})
      }
      const serverLang = document.documentElement.lang
      if (isLocale(serverLang)) return serverLang
      const langParam = new URLSearchParams(window.location.search).get('lang')
      if (isLocale(langParam)) return langParam
      const cookieLang = getCookie('language')
      if (isLocale(cookieLang)) return cookieLang
      return parseLocale({ 'accept-language': navigator.language })
    })(),
  },
  setStore: (store) =>
    set((state) => ({
      store: {
        ...state.store,
        ...store,
        customize: {
          ...state.store.customize,
          ...store.customize,
        },
      },
    })),
  newVersionAvailable: false,
  setNewVersionAvailable: (newVersionAvailable) => set({ newVersionAvailable }),
  hydrated: false,
  setHydrated: () => set({ hydrated: true }),
  radarStyle: storedRadarStyle(),
  setRadarStyle: (radarStyle) => {
    try {
      localStorage.setItem(RADAR_STYLE_KEY, radarStyle)
    } catch {
      // Not persisting is survivable; the switch still applies for this session.
    }
    set({ radarStyle })
  },
  peopleStepShown: false,
  markPeopleStepShown: () => set({ peopleStepShown: true }),
  // Always false: a reload re-arms the guide, and it is only read after hydration, so SSR cannot mismatch.
  welcomeDismissed: false,
  dismissWelcome: () => set({ welcomeDismissed: true }),
  resetWelcome: () => set({ welcomeDismissed: false }),
}))
